import { ReactNode, createContext, useCallback, useContext, useEffect, useRef } from 'react';

import { useOnceWhen } from '../../../../../../hooks';
import {
  MarketplaceSubEvent,
  OfferActionSurface,
  OfferActionType,
  OfferDashboardSubEvent,
  trackOfferDashboardEvent,
} from '../../../../../../services/analytics/marketplaceEvents';
import { MarketplaceOffer } from './utils';

// Per-event context the caller knows; the provider fills in session/timing/count properties.
export type TrackOfferEventArgs = {
  offerId?: string;
  dealerLabel?: string;
  dealerAmount?: number;
  driverAmount?: number;
  actionType?: OfferActionType;
  surface?: OfferActionSurface;
};

type OfferAnalyticsContextValue = {
  trackOfferEvent: (event: OfferDashboardSubEvent, args?: TrackOfferEventArgs) => void;
};

// Defaults to a no-op so a consumer rendered outside the provider silently does nothing rather
// than crashing — analytics is fire-and-forget and must never break a driver action.
const OfferAnalyticsContext = createContext<OfferAnalyticsContextValue>({
  trackOfferEvent: () => undefined,
});

export const useOfferAnalytics = () => useContext(OfferAnalyticsContext);

type OfferAnalyticsProviderProps = {
  enabled: boolean;
  leadId: string;
  offers: MarketplaceOffer[];
  pendingOfferCount: number;
  pendingCounterCount: number;
  children: ReactNode;
};

// Owns the dashboard-session analytics state — when the session started, when each offer first
// arrived (initial load or a later poll), and the live pending counts — so any descendant can fire
// an offer event with one call and the shared timing/count properties are assembled here.
export const OfferAnalyticsProvider = ({
  enabled,
  leadId,
  offers,
  pendingOfferCount,
  pendingCounterCount,
  children,
}: OfferAnalyticsProviderProps) => {
  const sessionStart = useRef(Date.now());
  const firstSeenByOfferId = useRef(new Map<string, number>());

  // Mirror live counts into a ref so the imperative tracker reads the values as of fire time.
  const countsRef = useRef({ pendingOfferCount, pendingCounterCount });
  countsRef.current = { pendingOfferCount, pendingCounterCount };

  useEffect(() => {
    const now = Date.now();
    offers.forEach((offer) => {
      if (!firstSeenByOfferId.current.has(offer.id)) {
        firstSeenByOfferId.current.set(offer.id, now);
      }
    });
  }, [offers]);

  const trackOfferEvent = useCallback(
    (event: OfferDashboardSubEvent, args: TrackOfferEventArgs = {}) => {
      if (!enabled) return;

      const now = Date.now();
      const firstSeen = args.offerId != null ? firstSeenByOfferId.current.get(args.offerId) : null;

      trackOfferDashboardEvent(event, {
        leadId,
        offerId: args.offerId,
        dealerLabel: args.dealerLabel,
        dealerAmount: args.dealerAmount,
        driverAmount: args.driverAmount,
        actionType: args.actionType,
        surface: args.surface,
        secondsSinceDashboardView: Math.round((now - sessionStart.current) / 1000),
        secondsSinceOfferArrived:
          firstSeen != null ? Math.round((now - firstSeen) / 1000) : undefined,
        pendingOfferCount: countsRef.current.pendingOfferCount,
        pendingCounterCount: countsRef.current.pendingCounterCount,
      });
    },
    [enabled, leadId],
  );

  useOnceWhen(enabled && leadId !== '', () =>
    trackOfferEvent(MarketplaceSubEvent.OfferDashboardViewed),
  );

  return (
    <OfferAnalyticsContext.Provider value={{ trackOfferEvent }}>
      {children}
    </OfferAnalyticsContext.Provider>
  );
};
